/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 11 2024
 **/

import { useEffect, useRef } from "react";
import { Properties } from "@/frameworks/libraries";
import useSound from "./useSound";

type SoundCommandType = {
	name: string;
	type: "play" | "pause" | "stop";
	src: string;
	playTime?: number;
};

export default function SoundProvider() {
	const call_audio_ref = useRef<HTMLAudioElement>(null);
	const call_close_audio_ref = useRef<HTMLAudioElement>(null);
	const { play, pause, stop } = useSound();

	useEffect(() => {
		Properties.on("sound_command", sound_command);
		return () => {
			Properties.off("sound_command", sound_command);
		};
	}, []);

	const sound_command = async ({ name, type, src, playTime }: SoundCommandType) => {
		// console.debug("#PLAY#", "PLAY with", name, src, type);
		try {
			switch (type) {
				case "play": {
					let played = false;
					if (src.includes("call.mp3")) {
						if (call_audio_ref.current) {
							await play(
								name,
								name === "phonecall_sound" ? call_audio_ref.current : new Audio(src),
								playTime
							);
							played = true;
						}
					}

					if (!played) {
						await play(name, new Audio(src), playTime);
					}
					break;
				}

				case "pause": {
					await pause(name);
					break;
				}

				case "stop": {
					// console.debug("#PLAY#", "STOP with", name);
					stop(name);
					break;
				}

				default: {
					break;
				}
			}
		} catch (error) {
			console.warn(error);
		}
	};

	return (
		<>
			<audio ref={call_audio_ref} preload="auto">
				<source src={`/audio/call.mp3?t=${Date.now()}`} />
			</audio>
			<audio ref={call_close_audio_ref} preload="auto">
				<source src={`/audio/call_close.mp3?t=${Date.now()}`} />
			</audio>
		</>
	);
}
